import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { cn } from '../lib/utils';
import {
  AppWindow,
  Calculator,
  Users,
  ExternalLink,
  X,
  RefreshCw,
  AlertCircle,
  Check,
  Clock,
  ArrowRight,
  Info,
  Shield,
  CreditCard,
  Sparkles,
  Loader2,
} from 'lucide-react';

const APP_ICONS = {
  'nexus-compta': Calculator,
  'nexus-sirh': Users,
  compta: Calculator,
  sirh: Users,
};

const APP_NAMES = {
  'nexus-compta': 'Nexus Compta',
  'nexus-sirh': 'Nexus SIRH',
};

const APP_URLS = {
  'nexus-compta': 'https://compta.nexus-360.fr',
  'nexus-sirh': 'https://sirh.nexus-360.fr',
};

const STATUS_LABELS = {
  active: { label: 'Abonné', className: 'bg-green-100 text-green-700' },
  trialing: { label: 'Essai', className: 'bg-yellow-100 text-yellow-700' },
  trial: { label: 'Essai', className: 'bg-yellow-100 text-yellow-700' },
  role_only: { label: 'Accès actif', className: 'bg-blue-100 text-blue-700' },
};

const AppCatalog = () => {
  const navigate = useNavigate();
  const { user, fetchUser } = useAuth();
  const [apps, setApps] = useState([]);
  const [packages, setPackages] = useState([]);
  const [detailedSubs, setDetailedSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailModal, setDetailModal] = useState(null);
  const [trialing, setTrialing] = useState(null);

  const pickTrialPackage = (appCode) => {
    const appPkgs = packages.filter((p) => (p.app_code || p.app?.code) === appCode);
    if (appPkgs.length === 0) return null;
    const popular = appPkgs.find((p) => p.popular || p.code === 'pro');
    if (popular) return popular;
    return [...appPkgs].sort((a, b) => (a.price_monthly_ht || 0) - (b.price_monthly_ht || 0))[0];
  };

  const handleStartTrial = async (appCode) => {
    const pkg = pickTrialPackage(appCode);
    if (!pkg) {
      toast.error("Aucun forfait disponible pour cette app");
      return;
    }
    setTrialing(appCode);
    try {
      const res = await api.post('/api/subscriptions/start-trial', {
        app_code: appCode,
        package_code: pkg.code,
      });
      const newToken = res.data?.access_token;
      if (newToken) {
        localStorage.setItem('token', newToken);
        api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        await fetchUser();
      }
      toast.success("Essai gratuit de 7 jours active");
      setDetailModal(null);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de demarrer l'essai");
    } finally {
      setTrialing(null);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [appsRes, pkgRes, subsRes] = await Promise.allSettled([
        api.get('/api/apps'),
        api.get('/api/subscriptions/packages'),
        api.get('/api/subscriptions/current-detailed'),
      ]);
      if (appsRes.status === 'fulfilled') {
        const data = appsRes.value.data;
        setApps(Array.isArray(data) ? data : data.items || []);
      }
      if (pkgRes.status === 'fulfilled') {
        const data = pkgRes.value.data;
        setPackages(Array.isArray(data) ? data : data.items || []);
      }
      if (subsRes.status === 'fulfilled') {
        const data = subsRes.value.data;
        setDetailedSubs(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      setError('Impossible de charger le catalogue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Determine access: subscription OR app_role
  const getAppAccess = (appCode) => {
    const detailed = detailedSubs.find((d) => d.app_code === appCode);
    if (detailed?.is_subscribed) {
      const status = detailed.app_subscription?.status || detailed.nexus_subscription?.status || 'active';
      return { hasAccess: true, status, detailed };
    }
    // Check app_roles
    const appRole = user?.app_roles?.[appCode];
    if (appRole) {
      return { hasAccess: true, status: 'role_only', detailed, role: typeof appRole === 'object' ? appRole.role : appRole };
    }
    return { hasAccess: false, status: null, detailed: null };
  };

  const getPackagesForApp = (appCode) =>
    packages.filter((p) => (p.app_code || p.app?.code) === appCode);

  const getLowestPrice = (appCode) => {
    const appPkgs = getPackagesForApp(appCode);
    if (appPkgs.length === 0) return null;
    const prices = appPkgs.map((p) => p.price_monthly_ht).filter((p) => p != null && p > 0);
    if (prices.length === 0) return null;
    return Math.min(...prices);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-slate-100 rounded w-48 animate-pulse" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-6 animate-pulse">
              <div className="w-14 h-14 bg-slate-100 rounded-xl mb-4" />
              <div className="h-5 bg-slate-100 rounded w-32 mb-2" />
              <div className="h-4 bg-slate-100 rounded w-full mb-1" />
              <div className="h-4 bg-slate-100 rounded w-3/4" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Erreur</h3>
        <p className="text-slate-500 mb-6">{error}</p>
        <button onClick={fetchData} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-nexus-600 text-white text-sm font-medium hover:bg-nexus-700 transition-colors">
          <RefreshCw className="w-4 h-4" /> Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Catalogue d'applications</h1>
        <p className="text-slate-500 text-sm mt-1">Découvrez et souscrivez aux applications Nexus</p>
      </div>

      {/* App Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {apps.map((app) => {
          const access = getAppAccess(app.code);
          const isComingSoon = app.status === 'coming_soon';
          const AppIcon = APP_ICONS[app.code] || AppWindow;
          const lowestPrice = getLowestPrice(app.code);
          const statusInfo = STATUS_LABELS[access.status] || null;
          const appUrl = app.url || app.base_url || APP_URLS[app.code] || '#';
          const packageInfo = access.detailed?.package;

          return (
            <div
              key={app.id || app.code}
              className={cn(
                'bg-white rounded-xl border overflow-hidden transition-all group',
                isComingSoon
                  ? 'border-slate-200 opacity-75'
                  : 'border-slate-200 shadow-sm hover:shadow-lg hover:border-nexus-200'
              )}
            >
              {/* Header */}
              <div className={cn(
                'h-32 relative flex items-center justify-center',
                access.hasAccess
                  ? 'bg-gradient-to-br from-nexus-500 to-nexus-700'
                  : isComingSoon
                  ? 'bg-gradient-to-br from-slate-200 to-slate-300'
                  : 'bg-gradient-to-br from-slate-100 to-slate-200'
              )}>
                <AppIcon className={cn('w-12 h-12', access.hasAccess ? 'text-white/80' : 'text-slate-400')} />

                {statusInfo && (
                  <div className="absolute top-3 right-3">
                    <span className={cn('flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full', statusInfo.className)}>
                      <Check className="w-3 h-3" /> {statusInfo.label}
                    </span>
                  </div>
                )}
                {isComingSoon && (
                  <div className="absolute top-3 right-3">
                    <span className="flex items-center gap-1 bg-amber-100 text-amber-700 text-xs font-medium px-2.5 py-1 rounded-full">
                      <Clock className="w-3 h-3" /> Bientôt disponible
                    </span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-5">
                <h3 className="text-lg font-semibold text-slate-900 mb-1">
                  {app.name || app.nom || APP_NAMES[app.code] || app.code}
                </h3>
                <p className="text-sm text-slate-500 line-clamp-3 mb-4">
                  {app.description || 'Application professionnelle Nexus'}
                </p>

                {/* Subscription info */}
                {access.hasAccess && packageInfo && (
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 text-xs space-y-1">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Forfait</span>
                      <span className="font-medium text-slate-900">{packageInfo.name}</span>
                    </div>
                    {packageInfo.price_monthly_ht > 0 && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Prix</span>
                        <span className="font-medium text-slate-900">{packageInfo.price_monthly_ht}€ HT/mois</span>
                      </div>
                    )}
                  </div>
                )}

                {access.hasAccess && access.role && (
                  <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-4">
                    <Shield className="w-3 h-3" />
                    Rôle : <span className="font-medium text-slate-700">{access.role}</span>
                  </div>
                )}

                {/* Price for non-subscribed */}
                {!access.hasAccess && lowestPrice != null && !isComingSoon && (
                  <p className="text-sm text-slate-600 mb-4">
                    À partir de <span className="font-bold text-slate-900">{lowestPrice}€ HT</span>
                    <span className="text-slate-400"> / mois</span>
                  </p>
                )}

                {/* Actions */}
                <div className="flex items-center gap-2">
                  {!isComingSoon && (
                    <button
                      onClick={() => setDetailModal(app)}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                    >
                      <Info className="w-4 h-4" />
                      En savoir plus
                    </button>
                  )}
                  {access.hasAccess ? (
                    <a
                      href={`${appUrl}/dashboard?token=${localStorage.getItem('token') || ''}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-nexus-600 text-white text-sm font-medium hover:bg-nexus-700 transition-colors"
                    >
                      Ouvrir <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  ) : isComingSoon ? (
                    <span className="flex-1 text-center text-sm text-slate-400 py-2">Prochainement</span>
                  ) : (
                    <button
                      onClick={() => handleStartTrial(app.code)}
                      disabled={trialing === app.code}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                    >
                      {trialing === app.code ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Sparkles className="w-3.5 h-3.5" />
                      )}
                      Essai 7j gratuit
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {apps.length === 0 && (
          <div className="sm:col-span-2 lg:col-span-3 bg-white rounded-xl border border-slate-200 p-12 text-center shadow-sm">
            <AppWindow className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Aucune application</h3>
            <p className="text-slate-500">Le catalogue est vide pour le moment.</p>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {detailModal && (() => {
        const access = getAppAccess(detailModal.code);
        const appPkgs = getPackagesForApp(detailModal.code);
        const DetailIcon = APP_ICONS[detailModal.code] || AppWindow;
        const appUrl = detailModal.url || detailModal.base_url || APP_URLS[detailModal.code] || '#';
        const appSub = access.detailed?.app_subscription;
        const appPkg = access.detailed?.package;

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setDetailModal(null)} />
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-nexus-50 flex items-center justify-center">
                    <DetailIcon className="w-5 h-5 text-nexus-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">
                      {detailModal.name || detailModal.nom || APP_NAMES[detailModal.code]}
                    </h3>
                    {access.hasAccess && (
                      <span className={cn('text-xs font-medium px-2 py-0.5 rounded-full', STATUS_LABELS[access.status]?.className || 'bg-green-100 text-green-700')}>
                        {STATUS_LABELS[access.status]?.label || 'Actif'}
                      </span>
                    )}
                  </div>
                </div>
                <button onClick={() => setDetailModal(null)} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                <p className="text-slate-600">{detailModal.description || 'Application professionnelle Nexus.'}</p>

                {/* Current subscription info */}
                {access.hasAccess && appSub && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                      <CreditCard className="w-4 h-4" /> Votre abonnement
                    </h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-green-600">Forfait</span>
                        <p className="font-medium text-green-900">{appPkg?.name || appSub.package_code}</p>
                      </div>
                      <div>
                        <span className="text-green-600">Statut</span>
                        <p className="font-medium text-green-900 capitalize">{appSub.status}</p>
                      </div>
                      {appSub.current_period_end && (
                        <div>
                          <span className="text-green-600">Valide jusqu'au</span>
                          <p className="font-medium text-green-900">
                            {new Date(appSub.current_period_end).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      )}
                      {appPkg?.price_monthly_ht > 0 && (
                        <div>
                          <span className="text-green-600">Prix</span>
                          <p className="font-medium text-green-900">{appPkg.price_monthly_ht}€ HT/mois</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Available packages */}
                {appPkgs.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3">
                      {access.hasAccess ? 'Tous les forfaits' : 'Forfaits disponibles'}
                    </h4>
                    <div className={cn('grid gap-3', appPkgs.length >= 3 ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2')}>
                      {appPkgs.map((pkg) => {
                        const isCurrent = access.hasAccess && (appSub?.package_code === pkg.code);
                        const isPro = pkg.code === 'pro' || pkg.popular;
                        return (
                          <div key={pkg.code || pkg.id} className={cn(
                            'border rounded-xl p-4 relative',
                            isCurrent ? 'border-green-400 bg-green-50' :
                            isPro ? 'border-nexus-400 shadow-md' : 'border-slate-200'
                          )}>
                            {isCurrent && (
                              <span className="absolute -top-2.5 left-3 bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                                Actuel
                              </span>
                            )}
                            {isPro && !isCurrent && (
                              <span className="absolute -top-2.5 left-3 bg-nexus-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                                Populaire
                              </span>
                            )}
                            <h5 className="font-semibold text-slate-900 mt-1">{pkg.name || pkg.nom}</h5>
                            <p className="text-2xl font-bold text-slate-900 mt-2">
                              {pkg.price_monthly_ht || 0}€
                              <span className="text-sm font-normal text-slate-500"> HT/mois</span>
                            </p>
                            {pkg.modules_labels && Object.keys(pkg.modules_labels).length > 0 ? (
                              <ul className="mt-3 space-y-1.5">
                                {Object.entries(pkg.modules_labels).slice(0, 6).map(([code, label]) => (
                                  <li key={code} className="text-xs text-slate-600 flex items-center gap-1.5">
                                    <Check className="w-3 h-3 text-green-500 flex-shrink-0" /> {label}
                                  </li>
                                ))}
                              </ul>
                            ) : pkg.modules && pkg.modules.length > 0 ? (
                              <ul className="mt-3 space-y-1.5">
                                {pkg.modules.slice(0, 6).map((m, i) => (
                                  <li key={i} className="text-xs text-slate-600 flex items-center gap-1.5">
                                    <Check className="w-3 h-3 text-green-500 flex-shrink-0" /> {typeof m === 'string' ? m : m.name || m}
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                            {pkg.max_users && (
                              <p className="text-xs text-slate-400 mt-2">Jusqu'à {pkg.max_users} utilisateurs</p>
                            )}
                            {pkg.max_employees && (
                              <p className="text-xs text-slate-400 mt-2">Jusqu'à {pkg.max_employees} employés</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200">
                <button onClick={() => setDetailModal(null)} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50">
                  Fermer
                </button>
                {access.hasAccess ? (
                  <a
                    href={`${appUrl}/dashboard?token=${localStorage.getItem('token') || ''}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-nexus-600 text-white text-sm font-medium hover:bg-nexus-700"
                  >
                    Ouvrir l'application <ExternalLink className="w-4 h-4" />
                  </a>
                ) : detailModal.status !== 'coming_soon' && (
                  <>
                    <button
                      onClick={() => handleStartTrial(detailModal.code)}
                      disabled={trialing === detailModal.code}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
                    >
                      {trialing === detailModal.code ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Sparkles className="w-4 h-4" />
                      )}
                      Essai gratuit 7 jours
                    </button>
                    <button
                      onClick={() => { setDetailModal(null); navigate('/subscriptions'); }}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg bg-nexus-600 text-white text-sm font-medium hover:bg-nexus-700"
                    >
                      Souscrire <ArrowRight className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default AppCatalog;
